import React, { useState, useEffect } from 'react';
import Button from '../../common/UI/Button';
import { Code, Coffee, Terminal, Zap } from 'lucide-react';
import { useExperience } from '../../../context/ExperienceContext';

const phrases = [
    'Full Stack Developer',
    'React Enthusiast', 
    'Problem Solver',
    'Code Wizard'
  ];
  
const HeroContent = () => {
  const [typedText, setTypedText] = useState('');
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const { yearsOfExperience } = useExperience();



  useEffect(() => {
    const phrase = phrases[currentPhrase];
    let i = 0;
    const typing = setInterval(() => {
      if (i <= phrase.length) {
        setTypedText(phrase.slice(0, i));
        i++;
      } else {
        clearInterval(typing);
        setTimeout(() => {
          const erasing = setInterval(() => {
            if (i > 0) {
              setTypedText(phrase.slice(0, i));
              i--;
            } else {
              clearInterval(erasing);
              setCurrentPhrase((prev) => (prev + 1) % phrases.length);
            }
          }, 50);
        }, 2000);
      }
    }, 100);

    return () => clearInterval(typing);
  }, [currentPhrase]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="text-center lg:text-left">
     

      {/* Name */}
      <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
        <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Anie Joseph
        </span>
        <br />
        <span className="text-gray-900 dark:text-white">Cabahug</span>
      </h1>

      {/* Animated Tagline */}
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl lg:text-3xl text-gray-600 dark:text-gray-300 mb-2">
          <span className="text-blue-600 dark:text-blue-400 font-semibold">
            {typedText}
          </span>
          <span className="animate-pulse text-blue-600 dark:text-blue-400">|</span>
        </h2>
        <p className="text-lg text-gray-500 dark:text-gray-400">
          Crafting digital experiences one line of code at a time
        </p>
      </div>

      {/* Description */}
      <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
        Passionate about building modern web applications with cutting-edge technologies. 
        I turn coffee into code and ideas into interactive experiences that users love.
      </p>

      {/* Tech Icons */}
      <div className="flex items-center justify-center lg:justify-start space-x-4 mb-8">
        <div className="flex items-center space-x-2 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg px-3 py-2">
          <Code className="w-5 h-5 text-blue-500" />
          <span className="text-sm text-gray-600 dark:text-gray-300">Clean Code</span>
        </div>
        <div className="flex items-center space-x-2 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg px-3 py-2">
          <Terminal className="w-5 h-5 text-green-500" />
          <span className="text-sm text-gray-600 dark:text-gray-300">Fast Builds</span>
        </div>
        <div className="flex items-center space-x-2 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg px-3 py-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          <span className="text-sm text-gray-600 dark:text-gray-300">Performance</span>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
        <Button
          onClick={() => scrollToSection('projects')}
          size="lg"
          className="group"
        >
          View My Work
          <Code className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
        </Button>
        <Button
          variant="outline"
          onClick={() => scrollToSection('contact')}
          size="lg"
          className="group"
        >
          Let's Connect
          <Coffee className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center lg:text-left">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{yearsOfExperience.displayText}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Years Experience</div>
        </div>
        <div className="text-center lg:text-left">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">20+</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Projects Built</div>
        </div>
        <div className="text-center lg:text-left">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">∞</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Cups of Coffee</div>
        </div>
      </div>
    </div>
  );
};

export default HeroContent;